import { useState, useEffect } from "react";
import "@/App.css";
import "@/custom.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import FullLengthTests from "@/pages/FullLengthTests";
import TestInterface from "@/pages/TestInterface";
import TestResults from "@/pages/TestResults";
import TestReview from "@/pages/TestReview";
import ReviewModuleSelect from "@/pages/ReviewModuleSelect";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ContactUs from "@/pages/ContactUs";
import DiagnosticTest from "@/pages/DiagnosticTest";
import AdminDashboard from "@/pages/AdminDashboard";
import DiagnosticResults from "@/pages/DiagnosticResults";
import Analytics from "@/pages/Analytics";
import DiagnosticPDF from "@/pages/DiagnosticPDF";
import ResetPassword from "@/pages/ResetPassword";
import QuickQuiz from "@/pages/QuickQuiz";
import QuickQuizResults from "@/pages/QuickQuizResults";
import Practice from "@/pages/Practice";
import PracticeSession from "@/pages/PracticeSession";
import PracticeResults from "@/pages/PracticeResults";
import PersonalizedPractice from "@/pages/PersonalizedPractice";
import MistakeJournal from "@/pages/MistakeJournal";
import SATTricks from "@/pages/SATTricks";
import LinearEquationsInequalities from "@/pages/subtopics/LinearEquationsInequalities";
import SystemsOfEquations from "@/pages/subtopics/SystemsOfEquations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set axios defaults
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
    // Normalize `error.response.data.detail` so components never receive an
    // object as a React child (crashes with minified React error #31). Some
    // backend endpoints return structured detail like {error, message} for
    // the mobile client — flatten to a plain string for web consumers while
    // preserving the original in `detailRaw` for anyone who needs it.
    const detail = error.response?.data?.detail;
    if (detail && typeof detail === 'object') {
      error.response.data.detailRaw = detail;
      error.response.data.detail =
        detail.message || detail.error || JSON.stringify(detail);
    }
    return Promise.reject(error);
  }
);

export { API, toast };

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const PrivateRoute = ({ children }) => {
    if (loading) return <div className="loading-screen">Loading...</div>;
    return user ? children : <Navigate to="/" />;
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="App">
      <Toaster 
        position="top-center" 
        toastOptions={{
          duration: 3000,
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage setUser={setUser} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/sat-tricks" element={<SATTricks />} />
          <Route path="/sat-tricks/linear-equations-inequalities" element={<LinearEquationsInequalities />} />
          <Route path="/sat-tricks/systems-of-equations" element={<SystemsOfEquations />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/diagnostic-test" element={<DiagnosticTest />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/diagnostic-results" element={<DiagnosticResults />} />
          <Route path="/diagnostic-pdf" element={<DiagnosticPDF />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/quick-quiz" element={<QuickQuiz />} />
          <Route path="/quick-quiz/results" element={<QuickQuizResults />} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard user={user} setUser={setUser} /></PrivateRoute>} />
          <Route path="/tests" element={<PrivateRoute><FullLengthTests user={user} setUser={setUser} /></PrivateRoute>} />
          <Route path="/test/:testId" element={<PrivateRoute><TestInterface user={user} /></PrivateRoute>} />
          <Route path="/results/:sessionId" element={<PrivateRoute><TestResults user={user} /></PrivateRoute>} />
          <Route path="/review/:sessionId" element={<PrivateRoute><ReviewModuleSelect user={user} /></PrivateRoute>} />
          <Route path="/review/:sessionId/:section/:moduleNum" element={<PrivateRoute><TestReview user={user} /></PrivateRoute>} />
          <Route path="/analytics/:sessionId" element={<PrivateRoute><Analytics user={user} /></PrivateRoute>} />
          <Route path="/practice" element={<PrivateRoute><Practice user={user} setUser={setUser} /></PrivateRoute>} />
          <Route path="/practice/session" element={<PrivateRoute><PracticeSession user={user} /></PrivateRoute>} />
          <Route path="/practice-session/:sessionId" element={<PrivateRoute><PracticeSession user={user} /></PrivateRoute>} />
          <Route path="/practice/results" element={<PrivateRoute><PracticeResults user={user} /></PrivateRoute>} />
          <Route path="/personalized-practice" element={<PrivateRoute><PersonalizedPractice user={user} setUser={setUser} /></PrivateRoute>} />
          <Route path="/mistake-journal" element={<PrivateRoute><MistakeJournal user={user} /></PrivateRoute>} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
